/* GrowthOS — Change Owner modal for selected contacts.
   Mirror of the leads LeadsChangeOwnerModal UI; reassigning sets the OwnerId field
   across the selected contacts (apiServices.updateMultipleContactField({ ids,
   fieldName:'OwnerId', fieldValue })), exactly the call the legacy #myOwner modal
   made. Reuses the shared .gos-import-* / .gos-tkt-* / .gos-foot-* / .gos-btn-*
   shell classes. */
import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import apiServices from '../../services/service';

const PATHS = {
  refresh: 'M21 12a9 9 0 1 1-3-6.7L21 8M21 3v5h-5',
  x: 'M18 6 6 18M6 6l12 12',
  check: 'M20 6 9 17l-5-5',
  search: 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM21 21l-4.3-4.3',
};
const Ic = ({ name, size = 16, className, style }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d={PATHS[name]} />
  </svg>
);

const AVA_COLORS = ['#c96342', '#2e63a6', '#2c7a45', '#9a751c', '#6a4fae', '#b34480', '#54738f'];
const initials = (name) =>
  String(name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] || '')
    .join('')
    .toUpperCase() || '?';
const avaColor = (name) =>
  AVA_COLORS[
    String(name || '')
      .split('')
      .reduce((a, c) => a + c.charCodeAt(0), 0) % AVA_COLORS.length
  ];

const ContactsChangeOwnerModal = ({ onClose, contactIds = [], owners = [], onUpdated }) => {
  const [sel, setSel] = useState(null);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);

  const people = useMemo(
    () =>
      (owners || []).map((o) => ({
        id: o.userId,
        name: o.display_name || o.userName || o.name || 'Unknown',
        sub: o.email || o.roleName || o.userName || '',
      })),
    [owners]
  );
  const list = q.trim()
    ? people.filter((p) => (p.name + ' ' + p.sub).toLowerCase().includes(q.toLowerCase().trim()))
    : people;

  const submit = () => {
    if (!sel || loading) return;
    setLoading(true);
    apiServices
      .updateMultipleContactField({ ids: contactIds, fieldName: 'OwnerId', fieldValue: sel })
      .then((response) => {
        if (response.status === 200) {
          toast.success('Owner updated successfully!', { position: 'top-right' });
          if (onUpdated) onUpdated();
          onClose();
        } else {
          setLoading(false);
          const msgs = [];
          for (const key in response.errors) {
            if (response.errors.hasOwnProperty(key)) msgs.push(...response.errors[key]);
          }
          msgs.forEach((m) => toast.error(m, { position: 'top-right' }));
        }
      })
      .catch(() => {
        toast.error('An unexpected error occurred.', { position: 'top-right' });
        setLoading(false);
      });
  };

  return (
    <div className="gos-import-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !loading) onClose(); }}>
      <div className="gos-tkt-modal gos-bulk-modal" role="dialog" aria-modal="true" aria-label="Change contact owner">
        <div className="gos-import-head">
          <div className="gos-import-head-l">
            <span className="gos-import-step-ic"><Ic name="refresh" size={17} /></span>
            <div>
              <div className="gos-import-title">Change Owner</div>
              <div className="gos-import-sub">Reassign these contacts</div>
            </div>
          </div>
          <button type="button" className="gos-import-close" onClick={onClose} aria-label="Close" disabled={loading}>
            <Ic name="x" size={18} />
          </button>
        </div>

        <div className="gos-tkt-body">
          {contactIds.length > 0 && (
            <div className="gos-tkt-scope">
              <Ic name="check" size={13} />
              Applies to <strong>{contactIds.length} selected contact{contactIds.length > 1 ? 's' : ''}</strong>
            </div>
          )}
          <label className="gos-bulk-label">Assign to</label>
          {people.length > 8 && (
            <div className="gos-camp-search">
              <Ic name="search" size={16} />
              <input placeholder="Search owners…" value={q} onChange={(e) => setQ(e.target.value)} />
              {q ? (
                <button type="button" className="gos-camp-search-clear" onClick={() => setQ('')} aria-label="Clear">
                  <Ic name="x" size={14} />
                </button>
              ) : null}
            </div>
          )}
          <div className="gos-owner-list">
            {list.length === 0 ? (
              <div className="gos-camp-empty">
                <Ic name="search" size={22} />
                <div style={{ fontWeight: 600, fontSize: 13.5, marginTop: 8 }}>No owners found</div>
              </div>
            ) : (
              list.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className={'gos-owner-row' + (sel === p.id ? ' on' : '')}
                  onClick={() => setSel(p.id)}
                >
                  <span className="gos-bulk-ava" style={{ background: avaColor(p.name) }}>{initials(p.name)}</span>
                  <div className="gos-bulk-grow">
                    <div className="gos-owner-name">{p.name}</div>
                    {p.sub ? <div className="gos-owner-sub">{p.sub}</div> : null}
                  </div>
                  <span className="gos-opt-radio">{sel === p.id ? <span className="gos-opt-radio-dot" /> : null}</span>
                </button>
              ))
            )}
          </div>
        </div>

        <div className="gos-import-foot end">
          <div className="gos-foot-actions">
            <button type="button" className="gos-btn gos-btn-ghost" onClick={onClose} disabled={loading}>Cancel</button>
            <button type="button" className="gos-btn gos-btn-primary" onClick={submit} disabled={!sel || loading}>
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Reassigning…
                </>
              ) : (
                <>
                  <Ic name="check" size={16} /> Reassign {contactIds.length} contact{contactIds.length > 1 ? 's' : ''}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactsChangeOwnerModal;
